import React, { useState } from 'react'

import { format } from 'date-fns'
import Link from 'next/link'
import {
  HiBuildingOffice2,
  HiMiniCalendarDays,
  HiMiniClock,
  HiOutlineArrowTopRightOnSquare,
  HiOutlineExclamationTriangle,
  HiOutlineMagnifyingGlass,
  HiOutlineMapPin,
  HiOutlineQuestionMarkCircle,
} from 'react-icons/hi2'

import Button from '@/components/common/Button'
import { OrgPermissionGate } from '@/components/common/PermissionGate'
import { Skeleton } from '@/components/common/Skeleton'
import Spinner from '@/components/common/Spinner'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import { useCreateThreadFromOpportunity } from '@/components/home/dashboard/hooks'
import { getNewOpportunitiesUrl } from '@/components/home/dashboard/lib'
import { Surface } from '@/components/home/ui/Surface'
import {
  DueThisWeek,
  MatchesNaics,
  Relevance,
  isDueThisWeek,
} from '@/components/home/ui/Tag'

import { DEMO_URL } from '@/constants/general'

import { getPursuitTypeFromOpportunityType } from '@/helpers/pursuit'
import { labelRestates, removeMarkdownFormatting } from '@/helpers/string'

import { useSafePush } from '@/hooks/navigation/useSafePush'
import {
  type OpportunitySearchResult,
  useFilteredRecommendedOpportunities,
} from '@/hooks/opportunity/useRecommendedOpportunities'
import { useActiveOrg } from '@/hooks/organization/useActiveOrg'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useLatestPursuitSchemaId } from '@/hooks/pursuits/schemas/usePursuitSchemas'
import { useCreatePursuit } from '@/hooks/pursuits/useCreatePursuit'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'
import type { Organization } from '@/types/Organization'
import type { CreatePursuit } from '@/types/pursuit/Pursuit'

const MAX_NEW_OPPORTUNITIES = 5

const getOpportunityLink = (opportunity: OpportunitySearchResult) => {
  const type = opportunity.type
  switch (type) {
    case 'federal_contract':
      return `/federal-contracts/${opportunity.id}`
    case 'federal_grant':
      return `/federal-grants/${opportunity.id}`
    case 'sbir':
      return `/sbirs/${opportunity.id}`
    case 'sled_contract':
      return `/sled-contracts/${opportunity.id}`
    case 'dibbs_contract':
      return `/dibbs-contracts/${opportunity.id}`
    default:
      const exhaustiveCheck: never = type
      throw new Error(`Unknown opportunity type: ${exhaustiveCheck}`)
  }
}

const getBuyerFromOpportunity = (opportunity: OpportunitySearchResult) => {
  const type = opportunity.type
  switch (type) {
    case 'federal_contract':
      return opportunity.samgov_opportunity.department ?? null
    case 'federal_grant':
      return opportunity.grantsgov_opportunity.agency_name ?? null
    case 'sbir':
      return opportunity.sbir_opportunity.agency ?? null
    case 'sled_contract':
      return opportunity.statelocal_opportunity.buyer_name ?? null
    case 'dibbs_contract':
      return 'Defense Logistics Agency'
    default:
      const exhaustiveCheck: never = type
      throw new Error(`Unknown opportunity type: ${exhaustiveCheck}`)
  }
}

// SLED buyer names can be raw internal labels (e.g. "Work Unit #15"), so surface where the
// opportunity is alongside the buyer. Use the pipeline's flat `location` ("City of Austin, TX",
// "Alabama") — it already carries the most specific place. The parallel jurisdiction_names /
// jurisdiction_prefixes arrays are NOT index-aligned (prefixes drop nulls and the state sorts
// first), so reading index 0 would collapse local opportunities down to just their state.
const getLocationFromOpportunity = (
  opportunity: OpportunitySearchResult,
): string | null => {
  if (opportunity.type !== 'sled_contract') {
    return null
  }
  return opportunity.statelocal_opportunity.location ?? null
}

const getMatchedNaics = (
  opportunity: OpportunitySearchResult,
  organization: Organization,
) => {
  const type = opportunity.type
  const orgNaicsCodes = organization.naics_codes
  switch (type) {
    case 'federal_contract':
      if (
        orgNaicsCodes.some(
          (code) => code === opportunity.samgov_opportunity.naics_code,
        )
      ) {
        return opportunity.samgov_opportunity.naics_code ?? null
      }
      return null
    default:
      return null
  }
}

const getOpportunitySolicitationType = (
  opportunity: OpportunitySearchResult,
) => {
  const type = opportunity.type
  switch (type) {
    case 'federal_contract':
      return opportunity.samgov_opportunity.notice_type ?? null
    case 'federal_grant':
      return 'Federal Grant'
    case 'sbir':
      return 'SBIR/STTR'
    case 'sled_contract':
      return opportunity.statelocal_opportunity.type ?? null
    case 'dibbs_contract':
      return 'DIBBS Posting'
    default:
      const exhaustiveCheck: never = type
      throw new Error(`Unknown opportunity type: ${exhaustiveCheck}`)
  }
}

const NewOpportunitiesHeader: React.FC = () => {
  const { activeOrgId } = useActiveOrgId()

  return (
    <Surface.Header>
      <Surface.Header.Icon icon={HiOutlineMagnifyingGlass} />
      <Surface.Header.Title>
        <span className="hidden md:block">New AI-Matched Opportunities</span>
        <span className="block md:hidden">New Opportunities</span>
      </Surface.Header.Title>
      <Surface.Header.Menu>
        <div className="flex items-center gap-x-2">
          <Tooltip delayDuration={0}>
            <TooltipTrigger>
              <HiOutlineQuestionMarkCircle
                size={16}
                className="text-primary-500 dark:text-primary-400"
              />
            </TooltipTrigger>
            <TooltipContent className="max-w-[300px]">
              <span>
                Sweetspot AI uses your organization&apos;s website and
                description to find opportunities that are a good fit for your
                business. This is refreshed daily. If you&apos;re not getting
                the right results, try{' '}
                <Link
                  href={activeOrgId ? `/orgs/${activeOrgId}/profile` : '#'}
                  className="text-blue-400 dark:text-blue-600 hover:opacity-70"
                >
                  editing your organization&apos;s website and description
                </Link>
                , or reach out to support for more help.
              </span>
            </TooltipContent>
          </Tooltip>
          <OrgPermissionGate permission="write" fallback="hide">
            <Link
              href={activeOrgId ? `/orgs/${activeOrgId}/profile` : '#'}
              className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:opacity-70"
            >
              Edit settings
            </Link>
          </OrgPermissionGate>
        </div>
      </Surface.Header.Menu>
    </Surface.Header>
  )
}

const NewOpportunitiesSkeleton: React.FC = () => {
  return (
    <Surface>
      <NewOpportunitiesHeader />
      <Surface.Content className="space-y-4">
        <div className="flex items-center gap-2 rounded-md border border-primary-200 bg-primary-100 px-3 py-2 text-sm text-primary-700 dark:border-primary-800 dark:bg-primary-900 dark:text-primary-300">
          <Spinner className="w-3.5 h-3.5" />
          <span>Matching new opportunities to your organization...</span>
        </div>
        {Array.from({ length: MAX_NEW_OPPORTUNITIES }).map((_, index) => (
          <div
            className="w-full rounded-md border border-primary-200 bg-primary-50 p-3 dark:border-primary-800 dark:bg-primary-900"
            key={index}
          >
            <div className="flex flex-col gap-[10px] w-full">
              <div className="flex flex-col gap-[10px] w-full">
                <div className="flex flex-col md:flex-row items-start justify-between gap-[10px] w-full">
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-full max-w-[420px] rounded-full" />
                    <Skeleton className="h-5 w-3/4 max-w-[320px] rounded-full md:hidden" />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 md:mt-[2px]">
                    <Skeleton className="h-6 w-24 rounded-full" />
                    <Skeleton className="h-6 w-20 rounded-full" />
                  </div>
                </div>
                <div className="hidden md:block space-y-2">
                  <Skeleton className="h-3 w-full rounded-full" />
                  <Skeleton className="h-3 w-2/3 rounded-full" />
                </div>
              </div>
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between w-full gap-x-4 gap-y-4 md:gap-y-2">
                <div className="flex flex-wrap gap-x-3 gap-y-2">
                  <Skeleton className="h-4 w-44 rounded-full" />
                  <Skeleton className="h-4 w-28 rounded-full" />
                  <Skeleton className="h-4 w-24 rounded-full" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-6 w-28 rounded-md" />
                  <Skeleton className="h-6 w-16 rounded-md" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </Surface.Content>
    </Surface>
  )
}

interface NewOpportunityProps {
  opportunity: OpportunitySearchResult
}

const NewOpportunity: React.FC<NewOpportunityProps> = ({ opportunity }) => {
  const { user } = useAuthInfoWithThrow()
  const { activeOrgId } = useActiveOrgId()
  const { activeOrg } = useActiveOrg()

  const { mutateAsync: createPursuit, isPending: isCreatingPursuit } =
    useCreatePursuit({
      orgId: activeOrgId,
    })
  const { data: latestSchemaId } = useLatestPursuitSchemaId({
    orgId: activeOrgId,
  })
  const {
    mutateAsync: createOpportunityChatSession,
    isPending: isCreatingOpportunityChatSession,
  } = useCreateThreadFromOpportunity({
    orgId: activeOrgId,
    opportunity,
  })
  const { safePush } = useSafePush()

  const [pursuitId, setPursuitId] = useState<string | null>(
    opportunity.pursuit_id ?? null,
  )

  const buyer = getBuyerFromOpportunity(opportunity)
  const location = getLocationFromOpportunity(opportunity)
  // Buyer and location often name the same place ("Plainfield, Town of" vs "Plainfield, Town of,
  // CT", or "County of Amherst" vs "Amherst County, VA"). When they do, keep the location — it
  // carries the map pin and usually the state — and drop the redundant buyer.
  let showBuyer = !!buyer
  const locationLabel = location
  if (buyer && location && labelRestates(buyer, location)) {
    showBuyer = false
  }
  const solicitationType = getOpportunitySolicitationType(opportunity)
  const matchedNaics = activeOrg
    ? getMatchedNaics(opportunity, activeOrg)
    : null
  const opportunityDueThisWeek =
    opportunity.active_until &&
    isDueThisWeek(new Date(opportunity.active_until), true)

  const showTags =
    (opportunity.relevance_tier !== 'low' &&
      opportunity.relevance_tier !== null) ||
    matchedNaics ||
    opportunityDueThisWeek

  const handleCreatePursuit = async () => {
    if (!activeOrgId) {
      return
    }
    try {
      const type = getPursuitTypeFromOpportunityType(opportunity.type)
      const req: CreatePursuit = {
        title: opportunity.title,
        due_date: opportunity.active_until
          ? new Date(opportunity.active_until)
          : new Date(),
        organization_id: activeOrgId,
        type,
        estimated_value: 0,
        pwin: 0,
        pwin_override: null,
        user_ids: [user.userId as UUID],
        description: opportunity.description,
        extended_schema_id: latestSchemaId ?? null,
        [type]: {
          entity_id: opportunity.id,
        },
      }
      const newPursuit = await createPursuit(req)
      setPursuitId(newPursuit.id)
    } catch (error) {
      console.error(error)
    }
  }

  const handleCreateOpportunityChatSession = async () => {
    const session = await createOpportunityChatSession()
    if (session && activeOrgId) {
      safePush(`/orgs/${activeOrgId}/c/opp/${session.id}`)
    }
  }

  return (
    <div className="w-full bg-primary-50 dark:bg-primary-900 divide-y divide-primary-200 dark:divide-primary-800 rounded-md p-3 hover:bg-primary-100 dark:hover:bg-primary-800/50 cursor-pointer transition-colors border border-primary-200 dark:border-primary-800">
      <div className="flex items-start gap-[10px] w-full">
        <div className="flex flex-col items-start justify-between gap-3 md:gap-[10px] w-full">
          <Link href={getOpportunityLink(opportunity)} className="w-full">
            <div className="flex flex-col gap-[10px] w-full">
              <div className="flex flex-col md:flex-row items-start justify-between gap-[10px] w-full">
                <span className="flex-1 block font-medium text-primary-800 dark:text-primary-100 !line-clamp-2">
                  {opportunity.title}
                </span>
                {showTags && (
                  <div className="flex flex-wrap items-center gap-2 md:mt-[2px]">
                    {opportunityDueThisWeek && <DueThisWeek />}
                    {matchedNaics && <MatchesNaics naics={matchedNaics} />}
                    {opportunity.relevance_tier &&
                      opportunity.relevance_tier !== 'low' && (
                        <Relevance relevance={opportunity.relevance_tier} />
                      )}
                  </div>
                )}
              </div>
              {opportunity.description && (
                <div className="hidden md:block">
                  <span className="text-[13px] text-primary-600 dark:text-primary-400 line-clamp-1">
                    {solicitationType && (
                      <span className="font-medium text-primary-700 dark:text-primary-300">
                        {solicitationType} {`\u2022`}{' '}
                      </span>
                    )}
                    {removeMarkdownFormatting(opportunity.description)}
                  </span>
                </div>
              )}
            </div>
          </Link>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between w-full gap-x-4 gap-y-4 md:gap-y-2">
            <div className="flex flex-wrap text-xs text-primary-700 dark:text-primary-200 gap-x-3 gap-y-2">
              {showBuyer && buyer && (
                <span className="flex items-center font-medium">
                  <HiBuildingOffice2
                    size={13}
                    className="mr-1 mb-[1px] text-blue-400 dark:text-blue-500"
                  />
                  {buyer}
                </span>
              )}
              {locationLabel && (
                <span className="flex items-center font-medium">
                  <HiOutlineMapPin
                    size={13}
                    className="mr-1 mb-[1px] text-primary-400 dark:text-primary-500"
                  />
                  {locationLabel}
                </span>
              )}
              {opportunity.posted_date && (
                <span className="flex items-center">
                  <HiMiniCalendarDays
                    size={13}
                    className="mr-1 text-green-500 dark:text-green-600"
                  />
                  <span>
                    Posted:{' '}
                    <span className="font-medium">
                      {format(new Date(opportunity.posted_date), 'MMM d, yyyy')}
                    </span>
                  </span>
                </span>
              )}
              {opportunity.active_until && (
                <>
                  <span className="flex items-center">
                    <HiMiniClock
                      size={13}
                      className="mr-1 text-red-400 dark:text-red-600"
                    />
                    <span>
                      Due:{' '}
                      <span className="font-medium">
                        {format(
                          new Date(opportunity.active_until),
                          'MMM d, yyyy',
                        )}
                      </span>
                    </span>
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {pursuitId ? (
                <Button
                  variant="positive-compact"
                  className="h-6 text-xs font-medium"
                >
                  <Link href={`/orgs/${activeOrgId}/pursuits/${pursuitId}`}>
                    <div className="flex items-center gap-x-[6px]">
                      <HiOutlineArrowTopRightOnSquare size={14} />
                      View Pursuit
                    </div>
                  </Link>
                </Button>
              ) : (
                <OrgPermissionGate permission="write" fallback="hide">
                  <Button
                    variant="outline-compact"
                    className="h-6 text-xs font-medium dark:hover:bg-primary-700"
                    isLoading={isCreatingPursuit}
                    onClick={handleCreatePursuit}
                  >
                    Add to Pursuits
                  </Button>
                </OrgPermissionGate>
              )}
              <OrgPermissionGate permission="write" fallback="hide">
                <Button
                  variant="primary-compact"
                  className="h-6 text-xs font-medium"
                  isLoading={isCreatingOpportunityChatSession}
                  disabled={isCreatingOpportunityChatSession}
                  onClick={handleCreateOpportunityChatSession}
                >
                  Ask AI
                </Button>
              </OrgPermissionGate>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const NoNewOpportunities: React.FC = () => {
  const { activeOrg } = useActiveOrg()
  const newOpportunitiesUrl = activeOrg
    ? getNewOpportunitiesUrl(activeOrg)
    : '#'
  return (
    <Surface.Placeholder className="py-8">
      <div className="flex flex-col items-center text-center gap-y-2">
        <span className="flex items-center gap-x-2 text-md font-semibold text-primary-900 dark:text-primary-100">
          <HiOutlineMagnifyingGlass
            size={16}
            className="text-green-600 dark:text-green-500"
          />
          No new opportunities for your organization.
        </span>{' '}
        <span className="text-[13px] font-medium text-primary-500 dark:text-primary-400">
          <Link
            className="text-blue-600 dark:text-blue-400 hover:opacity-70"
            href={newOpportunitiesUrl}
          >
            Click here to search for new opportunities
          </Link>
          .
        </span>
      </div>
    </Surface.Placeholder>
  )
}

const NoHighlyRelevantOpportunities: React.FC = () => {
  return (
    <div className="flex items-center justify-center gap-[10px] w-full rounded-md bg-yellow-100 dark:bg-yellow-800/50 border border-yellow-300 dark:border-yellow-700 py-3 px-3">
      <span className="text-center leading-5 text-sm font-medium text-yellow-900 dark:text-yellow-100">
        We couldn&apos;t find any highly relevant new opportunities for your
        organization. Please check back tomorrow!
      </span>
    </div>
  )
}

const TrialExpired: React.FC = () => {
  return (
    <Surface.Placeholder className="py-8 px-8 md:px-12">
      <div className="flex flex-col items-center text-center gap-y-2">
        <span className="flex items-center gap-x-2 text-md font-semibold text-primary-900 dark:text-primary-100">
          <HiOutlineExclamationTriangle
            size={16}
            className="text-red-600 dark:text-red-500"
          />
          Your trial has expired.
        </span>{' '}
        <span className="text-[13px] font-medium text-primary-500 dark:text-primary-400">
          <Link
            className="text-blue-600 dark:text-blue-400 hover:opacity-70"
            href="/upgrade"
            target="_blank"
          >
            Click here to upgrade
          </Link>
          , or{' '}
          <Link
            className="text-blue-600 dark:text-blue-400 hover:opacity-70"
            href={DEMO_URL}
            target="_blank"
          >
            book a demo
          </Link>{' '}
          to get a full overview of how Sweetspot can help your business grow.
        </span>
      </div>
    </Surface.Placeholder>
  )
}

interface MissingOrganizationProfileProps {}

const MissingOrganizationProfile: React.FC<
  MissingOrganizationProfileProps
> = () => {
  const { activeOrg } = useActiveOrg()

  // if the description is less than 5 words, don't show the warning
  if (
    !activeOrg ||
    (activeOrg.description !== null &&
      activeOrg.description.trim().split(' ').length > 3)
  ) {
    return null
  }

  return (
    <div className="flex items-center justify-center gap-[10px] w-full text-yellow-900 dark:text-yellow-100 rounded-md bg-yellow-100 dark:bg-yellow-800/50 border border-yellow-300 dark:border-yellow-700 py-3 px-4">
      <HiOutlineExclamationTriangle size={16} className="flex-shrink-0" />
      <span className="leading-4 text-sm font-medium">
        Your organization is missing a description. Please{' '}
        <Link
          href={`/orgs/${activeOrg.id}/profile`}
          className="underline hover:opacity-70"
        >
          add one
        </Link>{' '}
        to improve your AI-matched opportunities.
      </span>
    </div>
  )
}

interface NewOpportunitiesProps {}

export const NewOpportunities: React.FC<NewOpportunitiesProps> = () => {
  const {
    opportunities,
    isLoading,
    trialExpired,
    showNoHighlyRelevantWarning,
  } = useFilteredRecommendedOpportunities({ maxCount: MAX_NEW_OPPORTUNITIES })

  if (trialExpired) {
    return (
      <Surface>
        <NewOpportunitiesHeader />
        <Surface.Content className="flex flex-col w-full gap-4">
          <TrialExpired />
        </Surface.Content>
      </Surface>
    )
  }

  if (isLoading) {
    return <NewOpportunitiesSkeleton />
  }

  return (
    <Surface>
      <NewOpportunitiesHeader />
      <Surface.Content className="flex flex-col w-full gap-4">
        <MissingOrganizationProfile />
        {showNoHighlyRelevantWarning && <NoHighlyRelevantOpportunities />}
        {opportunities.length > 0 ? (
          opportunities.map((opportunity) => (
            <NewOpportunity key={opportunity.id} opportunity={opportunity} />
          ))
        ) : (
          <NoNewOpportunities />
        )}
      </Surface.Content>
    </Surface>
  )
}

NewOpportunities.displayName = 'Home.NewOpportunities'
